import { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { queryClient } from '../lib/queryClient';
import { clearAllPendingActions } from '../lib/offlineQueue';
import { Profile } from '../types';
import { checkMfaRequired } from '../lib/queries/auth-security';

interface MfaPending {
  factorId: string;
  user: User;
  profile: Profile;
  session: Session;
}

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  session: Session | null;
  loading: boolean;
  mfaPending: MfaPending | null;
  /**
   * Why a restored session was dropped, for the login screen to show.
   *
   * The MFA gate fails closed on session restore, which is correct — but a silent sign-out
   * leaves the user staring at a login form with no idea why, and they will assume the app
   * is broken and retry forever. Blocking access has to come with a reason.
   */
  authError: string | null;
  clearAuthError: () => void;
  signIn: (username: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  completeMfa: () => void;
  cancelMfa: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

async function fetchProfile(userId: string, retries = 3): Promise<Profile | null> {
  for (let i = 0; i < retries; i++) {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    if (!error && data) return data as Profile;
    if (i < retries - 1) await new Promise(r => setTimeout(r, 500));
  }
  return null;
}

// A lookup that COULD NOT RUN is not a lookup that found nothing. Collapsing the
// two is what made LOGIN-UNAME-1 invisible for four days: `anon` lost EXECUTE on
// lookup_email_by_username, every call failed with a permission error, and every
// user was told their own username did not exist. They retyped it instead of
// reporting an outage. Keep the two apart.
type UsernameResolution =
  | { ok: true; email: string }
  | { ok: false; reason: 'not_found' | 'unavailable' };

// Resolution runs in an Edge Function, not an RPC. It needs the service key to read
// `profiles` for a caller with no session, and keeping that key server-side means
// `anon` needs no database privilege at all — so there is no grant to lose. Losing
// one is what caused LOGIN-UNAME-1. See supabase/functions/resolve-username.
async function resolveEmailFromUsername(username: string): Promise<UsernameResolution> {
  const { data, error } = await supabase.functions.invoke<{ email: string | null }>(
    'resolve-username',
    { body: { username } },
  );
  if (error) {
    console.error('[auth] username lookup unavailable:', error.message);
    return { ok: false, reason: 'unavailable' };
  }
  if (!data?.email) return { ok: false, reason: 'not_found' };
  return { ok: true, email: data.email };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [mfaPending, setMfaPending] = useState<MfaPending | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const signingIn = useRef(false);

  const clearAuthError = () => setAuthError(null);

  useEffect(() => {
    // Session restore must re-apply the MFA gate, not just trust that a session exists.
    //
    // signInWithPassword issues an aal1 session the moment the password is right — before
    // any code is entered. That session is persisted by supabase-js, so if this path simply
    // adopted it, pressing F5 at the TOTP prompt would reload straight into the app with no
    // second factor. The gate has to be re-evaluated from the token on every restore, and
    // for an aal2 session it costs nothing: `required` comes back false.
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!session?.user) { setLoading(false); return; }

      const p = await fetchProfile(session.user.id);

      try {
        const mfa = await checkMfaRequired();
        if (mfa.required && mfa.enrolled && mfa.factorId && p) {
          setMfaPending({ factorId: mfa.factorId, user: session.user, profile: p, session });
          setLoading(false);
          return;
        }
      } catch (mfaErr) {
        // Fail closed: an unresolvable gate is not a pass. Drop the session rather than
        // admit a user whose assurance level we could not read.
        console.error('MFA check failed on session restore:', mfaErr);
        // Fail closed, but say so. Same wording as the sign-in path, so the user sees one
        // consistent reason rather than a login form that silently ate their session.
        setAuthError('Security verification failed. Please try again or contact your administrator.');
        await supabase.auth.signOut();
        setLoading(false);
        return;
      }

      setSession(session);
      setUser(session.user);
      setProfile(p);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (signingIn.current) return;
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        (async () => {
          const p = await fetchProfile(session.user.id);
          setProfile(p);
        })();
      } else {
        setProfile(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (usernameOrEmail: string, password: string): Promise<{ error: string | null }> => {
    signingIn.current = true;
    setAuthError(null);
    try {
      let email = usernameOrEmail;

      if (!usernameOrEmail.includes('@')) {
        const resolved = await resolveEmailFromUsername(usernameOrEmail);
        if (!resolved.ok) {
          return {
            error: resolved.reason === 'unavailable'
              // Name the real condition and give the way through: an email address
              // skips this lookup entirely, so the user is not stranded.
              ? 'Sign-in by username is temporarily unavailable. Please sign in with your email address, or try again shortly.'
              // A miss must not confirm whether the name exists — same wording as a
              // wrong password below.
              : 'Invalid username or password.',
          };
        }
        email = resolved.email;
      }

      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        return { error: 'Invalid username or password.' };
      }

      const p = await fetchProfile(data.user.id);
      if (!p) return { error: 'Profile not found. Please contact your administrator.' };

      if (p.account_status === 'pending') {
        await supabase.auth.signOut();
        return { error: 'Your account is pending approval. Please contact your administrator.' };
      }
      if (p.account_status === 'rejected') {
        await supabase.auth.signOut();
        return { error: 'Your account has been rejected. Please contact your administrator.' };
      }
      if (p.account_status === 'inactive' || p.account_status === 'suspended') {
        await supabase.auth.signOut();
        return { error: 'Your account has been deactivated. Please contact your administrator.' };
      }

      // Check MFA requirement — decided entirely from the signed token (factors + aal).
      try {
        const mfa = await checkMfaRequired();
        if (mfa.required && mfa.enrolled && mfa.factorId) {
          // User has MFA — show verification modal before completing login
          setMfaPending({ factorId: mfa.factorId, user: data.user, profile: p, session: data.session });
          return { error: null };
        }
      } catch (mfaErr) {
        // MFA check failed — deny access for security (fail-closed)
        console.error('MFA check failed:', mfaErr);
        await supabase.auth.signOut();
        return { error: 'Security verification failed. Please try again or contact your administrator.' };
      }

      setSession(data.session);
      setUser(data.user);
      setProfile(p);
      return { error: null };
    } finally {
      signingIn.current = false;
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    queryClient.clear();
    // queryClient.clear() only drops the in-memory cache. The service worker also
    // persists API responses to disk, which survive sign-out — on a shared device
    // that would leave this user's finance/HR data cached for the next person.
    // The SW partitions per user, so this is defence in depth rather than the
    // primary control, but the data should not outlive the session either way.
    navigator.serviceWorker?.controller?.postMessage({ type: 'CLEAR_API_CACHES' });
    clearAllPendingActions().catch(() => {});
    setUser(null);
    setProfile(null);
    setSession(null);
  };

  const refreshProfile = async () => {
    if (!user) return;
    const p = await fetchProfile(user.id);
    setProfile(p);
  };

  const completeMfa = () => {
    if (mfaPending) {
      setSession(mfaPending.session);
      setUser(mfaPending.user);
      setProfile(mfaPending.profile);
      // Nothing is written to remember this. The challenge already upgraded the session to
      // aal2 server-side (mfa.challenge + mfa.verify), and that is what every later check
      // reads. A local "we did this" note would only be a forgeable copy of a fact the
      // token already carries — which is exactly the bug this PR removes.
      setMfaPending(null);
    }
  };

  const cancelMfa = async () => {
    setMfaPending(null);
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, profile, session, loading, mfaPending, authError, clearAuthError, signIn, signOut, refreshProfile, completeMfa, cancelMfa }}>
      {children}
    </AuthContext.Provider>
  );
}
